import { ExhibitionCollectionFragment } from '@/gql/generated/graphql'
import { ImageWithCustomLoader } from '@components/image-with-custom-loader'
import { BodyText, DetailText, HeadlineText, LeftArrowIcon } from 'design-configuration/components'
import React from 'react'
import { ReleaseTimer } from './release-timer'
import { useSoloViewer } from '@components/solo-viewer/provider'
import { TrackedButtonNames, trackButtonClicked } from '@utils/analytics'

type AboutCardProps = {
  title: string
  artist: string
  subtitle: string
  description: React.ReactElement
  topImage: string
  collection: ExhibitionCollectionFragment | null
  anchor: string
}

export const AboutCard = ({
  title,
  artist,
  subtitle,
  description,
  topImage,
  collection,
  anchor
}: AboutCardProps): React.ReactElement | null => {
  const { setSoloViewerData, setSoloViewerImageData } = useSoloViewer()
  const scrollTo = () => {
    document.getElementById(anchor)?.scrollIntoView({ behavior: 'smooth' })
  }
  // the assigned project is the digital collection
  const proj = collection?.Collection?.Project
  // we treat the first project in additional projects as the physical project
  const physicalProject = collection?.AdditionalProjects?.[0]?.Project
  if (!proj || !physicalProject) {
    return null
  }

  return (
    <div className="flex flex-col gap-y-[10px]">
      <div className="flex items-stretch justify-center lg:flex-col">
        <button
          className="relative aspect-square w-1/2 lg:w-full"
          onClick={() => setSoloViewerImageData({ imageList: [topImage], image: topImage })}
        >
          <ImageWithCustomLoader
            src={topImage}
            alt={`${title}-static-image`}
            style={{
              objectFit: 'cover'
            }}
            fill
            sizes="33vw"
          />
          {proj ? (
            <div className="top-xs left-xs absolute">
              <ReleaseTimer project={physicalProject} />
            </div>
          ) : null}
        </button>
        <div className="bg-surface-container p-md lg:p-2xl relative aspect-square w-1/2 lg:aspect-[4/3] lg:w-full">
          <button
            className="relative h-full w-full"
            onClick={() =>
              setSoloViewerData({ token: proj?.featured_token?.[0], tokenList: [...proj.tokens, ...proj.oob_tokens] })
            }
          >
            <div className="relative h-full w-full" style={{ filter: 'drop-shadow(3px 3px 6px rgba(0, 0, 0, 0.20))' }}>
              <ImageWithCustomLoader
                src={proj?.featured_token?.[0].media_url ?? ''}
                alt={`${title}-featured-token`}
                style={{
                  objectFit: 'contain'
                }}
                fill
                sizes="33vw"
              />
            </div>
          </button>
          {proj ? (
            <div className="top-xs left-xs absolute">
              <ReleaseTimer project={proj} />
            </div>
          ) : null}
        </div>
      </div>
      <div className="gap-y-xs px-md flex flex-col">
        <div className="flex flex-col">
          <HeadlineText className="font-medium">{title}</HeadlineText>
          <HeadlineText className="font-light">{artist}</HeadlineText>
        </div>
        <DetailText className="text-on-surface-variant font-medium uppercase">{subtitle}</DetailText>
        {description}
        <button
          onClick={() => {
            scrollTo()
            trackButtonClicked(TrackedButtonNames.exhibitionMoreInfo, {
              project: proj,
              customProperties: {
                'Project id': proj.id,
                'Project name': proj.name
              }
            })
          }}
          className="text-on-surface-variant gap-x-2xs flex items-center"
        >
          <BodyText className="underline">More information</BodyText>
          <LeftArrowIcon className="w-sm h-sm fill-on-surface-variant -rotate-90" />
        </button>
      </div>
    </div>
  )
}
